import { useQuery } from '@tanstack/react-query';
import { productMetricsApi } from '@/lib/api';
import { useState } from 'react';

// B2: Metricas de Producto - Funnel, Cohorts, Data Completeness
export default function ProductMetrics() {
  const [funnelDays, setFunnelDays] = useState(30);
  const [cohortWeeks, setCohortWeeks] = useState(8);

  const { data: funnelData, isLoading: funnelLoading } = useQuery({
    queryKey: ['productMetrics', 'funnel', funnelDays],
    queryFn: () => productMetricsApi.getFunnel(funnelDays),
  });

  const { data: cohortsData, isLoading: cohortsLoading } = useQuery({
    queryKey: ['productMetrics', 'cohorts', cohortWeeks],
    queryFn: () => productMetricsApi.getCohorts(cohortWeeks),
  });

  const { data: completenessData, isLoading: completenessLoading } = useQuery({
    queryKey: ['productMetrics', 'completeness'],
    queryFn: () => productMetricsApi.getCompleteness({ limit: 20 }),
  });

  const { data: featuresData, isLoading: featuresLoading } = useQuery({
    queryKey: ['productMetrics', 'features'],
    queryFn: productMetricsApi.getFeatures,
  });

  const funnel = funnelData?.data;
  const cohorts = cohortsData?.data;
  const completeness = completenessData?.data;
  const features = featuresData?.data;

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Metricas de Producto</h1>
          <p className="text-gray-500">Funnel de conversion, cohorts y completitud de datos</p>
        </div>
      </div>

      {/* Funnel Section */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Funnel de Conversion</h2>
          <select
            value={funnelDays}
            onChange={(e) => setFunnelDays(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            aria-label="Seleccionar periodo del funnel"
          >
            <option value={7}>Ultimos 7 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={90}>Ultimos 90 dias</option>
          </select>
        </div>

        {funnelLoading ? (
          <div className="text-center py-8 text-gray-500">Cargando funnel...</div>
        ) : funnel ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
              {funnel.funnel?.map((step: { step: string; count: number; rate: number }, index: number) => (
                <div
                  key={step.step}
                  className={`text-center p-3 rounded-lg ${
                    index === 0 ? 'bg-blue-100' : 'bg-gray-50'
                  }`}
                >
                  <p className="text-xs text-gray-500 mb-1">{step.step}</p>
                  <p className="text-xl font-bold text-gray-900">{step.count}</p>
                  <p className={`text-sm ${
                    step.rate >= 70 ? 'text-green-600' :
                    step.rate >= 40 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {step.rate}%
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Tasa de Conversion Total</p>
                <p className="text-sm text-gray-500">SignUp → Week-1 Active</p>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-bold ${
                  funnel.conversionRate >= 30 ? 'text-green-600' :
                  funnel.conversionRate >= 15 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {funnel.conversionRate}%
                </p>
                <p className="text-sm text-gray-500">de {funnel.totalSignups} signups</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">Sin datos de funnel</div>
        )}
      </div>

      {/* Features Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* Import Metrics */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Masivo</h3>
          {featuresLoading ? (
            <div className="text-center py-4 text-gray-500">Cargando...</div>
          ) : features?.import ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Jobs totales</span>
                <span className="font-medium">{features.import.totalJobs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Exitosos</span>
                <span className="text-green-600 font-medium">{features.import.success}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fallidos</span>
                <span className="text-red-600 font-medium">{features.import.failed}</span>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tasa de exito</span>
                  <span className={`font-bold ${
                    features.import.successRate >= 80 ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {features.import.successRate}%
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Hogar Metrics */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hogares Multi-Persona</h3>
          {featuresLoading ? (
            <div className="text-center py-4 text-gray-500">Cargando...</div>
          ) : features?.hogar ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Hogares creados</span>
                <span className="font-medium">{features.hogar.totalHogares}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Invitaciones enviadas</span>
                <span className="font-medium">{features.hogar.invitacionesSent}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Invitaciones aceptadas</span>
                <span className="text-green-600 font-medium">{features.hogar.invitacionesAccepted}</span>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tasa de aceptacion</span>
                  <span className={`font-bold ${
                    features.hogar.acceptRate >= 50 ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {features.hogar.acceptRate}%
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Chat AI Metrics */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Chat AI</h3>
          {featuresLoading ? (
            <div className="text-center py-4 text-gray-500">Cargando...</div>
          ) : features?.chat ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Sesiones totales</span>
                <span className="font-medium">{features.chat.totalSessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mensajes totales</span>
                <span className="font-medium">{features.chat.totalMessages}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Simulaciones</span>
                <span className="font-medium">{features.chat.simulations}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Simulaciones aplicadas</span>
                <span className="text-green-600 font-medium">{features.chat.simulationsApplied}</span>
              </div>
              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tasa de aplicacion</span>
                  <span className={`font-bold ${
                    features.chat.applyRate >= 20 ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {features.chat.applyRate}%
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Cohorts Section */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Cohorts por Semana de Registro</h2>
          <select
            value={cohortWeeks}
            onChange={(e) => setCohortWeeks(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            aria-label="Seleccionar numero de semanas para cohorts"
          >
            <option value={4}>4 semanas</option>
            <option value={8}>8 semanas</option>
            <option value={12}>12 semanas</option>
          </select>
        </div>

        {cohortsLoading ? (
          <div className="text-center py-8 text-gray-500">Cargando cohorts...</div>
        ) : cohorts?.cohorts?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cohorte</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Signups</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Semana 1</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Semana 2</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Semana 3</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Semana 4</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cohorts.cohorts.map((cohort: {
                  week: string;
                  weekStart: string;
                  signups: number;
                  retention: { week1: number; week2: number; week3: number; week4: number };
                }) => (
                  <tr key={cohort.week} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900">{cohort.week}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(cohort.weekStart).toLocaleDateString('es-MX')}
                      </p>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{cohort.signups}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        cohort.retention.week1 >= 50 ? 'bg-green-100 text-green-800' :
                        cohort.retention.week1 >= 25 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.retention.week1}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        cohort.retention.week2 >= 40 ? 'bg-green-100 text-green-800' :
                        cohort.retention.week2 >= 20 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.retention.week2}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        cohort.retention.week3 >= 30 ? 'bg-green-100 text-green-800' :
                        cohort.retention.week3 >= 15 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.retention.week3}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        cohort.retention.week4 >= 25 ? 'bg-green-100 text-green-800' :
                        cohort.retention.week4 >= 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {cohort.retention.week4}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">Sin datos de cohorts</div>
        )}
      </div>

      {/* Data Completeness Section */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Data Completeness Score</h2>
          {completeness && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                Promedio: <span className="font-bold text-gray-900">{completeness.averageScore}%</span>
              </span>
            </div>
          )}
        </div>

        {/* Distribution */}
        {completeness?.distribution && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{completeness.distribution.excellent}</p>
              <p className="text-xs text-gray-500">Excelente (80%+)</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{completeness.distribution.good}</p>
              <p className="text-xs text-gray-500">Bueno (60-79%)</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{completeness.distribution.fair}</p>
              <p className="text-xs text-gray-500">Regular (40-59%)</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{completeness.distribution.poor}</p>
              <p className="text-xs text-gray-500">Bajo (&lt;40%)</p>
            </div>
          </div>
        )}

        {completenessLoading ? (
          <div className="text-center py-8 text-gray-500">Cargando completeness...</div>
        ) : completeness?.hogares?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Hogar</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Cuentas</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Movs</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Gastos Fijos</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Tarjetas</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Metas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {completeness.hogares.map((h: {
                  hogarId: string;
                  nombre: string;
                  score: number;
                  miembros: number;
                  details: {
                    cuentas: number;
                    movimientos: number;
                    gastosFijos: number;
                    tarjetas: number;
                    metas: number;
                  };
                }) => (
                  <tr key={h.hogarId} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-900">{h.nombre}</p>
                      <p className="text-xs text-gray-500">{h.miembros} miembros</p>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${
                              h.score >= 80 ? 'bg-green-500' :
                              h.score >= 60 ? 'bg-blue-500' :
                              h.score >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${h.score}%` }}
                          />
                        </div>
                        <span className="font-medium text-gray-900">{h.score}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-gray-600">{h.details.cuentas}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{h.details.movimientos}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{h.details.gastosFijos}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{h.details.tarjetas}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{h.details.metas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">Sin datos de completeness</div>
        )}
      </div>
    </>
  );
}
